/* eslint import/namespace: [2, { allowComputed: true }] */
import * as d3 from 'd3';
import unitHelper from '@client/helper/unit.js';
import { Chart } from '../chart.js';
import { CHART_TYPE_UP_DOWN_HISTOGRAM, CHART_TYPE_PLURALITY_RECT } from '@client/pages/activity-detail/config/chart.types.js';

export class DistanceChart extends Chart {
  // 获取数据中最接近的x的值
  getSimilarXValue(value) {
    const that = this;
    return unitHelper.getNearlyData(that.data, that.xAxisKey, value);
  }

  // 创建y轴比例尺
  createYAxis(attr) {
    const that = this;

    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const scaleType = attr.scaleType;
    const valueData = [];
    that.data.forEach((item) => {
      item.data.forEach((d, i) => {
        valueData.push(getDataValue(attr, d, i));
      });
    });
    let min = Object.prototype.hasOwnProperty.call(attr, 'min') ? attr.min : d3.min(valueData) * 0.9;
    let max = Object.prototype.hasOwnProperty.call(attr, 'max') ? attr.max : d3.max(valueData) * 1.1;
    min = [undefined].includes(min) ? 0 : min;
    max = [undefined].includes(max) ? (min + 1) * 2 : max;
    if (min === max) {
      max = (min + 1) * 2;
    }

    that.yScales[chartName] = d3[scaleType || 'scaleLinear']()
      .domain([min, max])
      // .domain([max, min])
      .range([0, that.contentHeight]);
  }

  // 画图表
  drawChart(attr) {
    const that = this;

    switch (attr.chartType) {
      case CHART_TYPE_UP_DOWN_HISTOGRAM:
        that.drawUpDownHistogramChart(attr);
        break;
      case CHART_TYPE_PLURALITY_RECT:
        that.drawPluralityRect(attr);
        break;
    }
  }

  // 绘制上下柱状图
  oldShangXia(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    const rectWidth = (that.contentWidth / (that.chartData.length + 1)) * 0.7;
    const chart = that.content.select(`.${className}`);
    // 中间坐标值
    const centerCount = (that.yScales[chartName].domain()[0] + that.yScales[chartName].domain()[1]) / 2;
    // 当数据缺失时，自行填充上一个数据
    let lastY = that.yScales[chartName].domain()[0];
    const getTrueValue = (d, i) => {
      let value = getDataValue(attr, d, i);
      if (isNaN(value) || isNaN(that.yScales[chartName](value)) || [undefined].includes(value)) {
        value = lastY;
      }
      return value;
    };
    // 柱的公共属性设置
    const setHistogramAttr = (select) => {
      return select
        .attr('class', 'rect')
        .attr('x', (d, i) => {
          return that.xScale(d[that.xAxisKey]) - rectWidth / 2;
        })
        .attr('y', (d, i) => {
          const value = getTrueValue(d, i);
          lastY = value;
          if (value >= centerCount) {
            return that.contentHeight - that.yScales[chartName](value);
          } else {
            return that.contentHeight / 2;
          }
        })
        .attr('width', rectWidth)
        .attr('height', (d, i) => {
          const value = getTrueValue(d, i);
          lastY = value;
          if (value >= centerCount) {
            return that.yScales[chartName](value) - that.contentHeight / 2;
          } else {
            return that.contentHeight / 2 - that.yScales[chartName](value);
          }
        })
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d, i) => {
          const value = getTrueValue(d, i);
          lastY = value;
          if (value >= centerCount) {
            return that.getFillColor(attr.upColor || attr.color);
          } else {
            return that.getFillColor(attr.downColor || attr.color);
          }
        });
    };
    const setHistogramListener = (select) => {
      const duration = 100;
      return select
        .on('mouseover', function (e) {
          setHistogramAttr(d3.select(this).transition().duration(duration)).attr('fill', (d, i) => {
            const value = getTrueValue(d, i);
            lastY = value;
            if (value >= centerCount) {
              return that.getFillColor(attr.upColor || attr.color, 0.4);
            } else {
              return that.getFillColor(attr.downColor || attr.color, 0.4);
            }
          });
        })
        .on('mouseleave', function (e) {
          setHistogramAttr(d3.select(this).transition().duration(duration)).attr('fill', (d, i) => {
            const value = getTrueValue(d, i);
            lastY = value;
            if (value >= centerCount) {
              return that.getFillColor(attr.upColor || attr.color);
            } else {
              return that.getFillColor(attr.downColor || attr.color);
            }
          });
        });
    };
    // 没有创建过该属性的图表
    chart.remove();
    const group = that.content.append('g').attr('class', className);

    setHistogramAttr(setHistogramListener(group.selectAll('.rect').data(that.chartData).enter().append('rect')));
  }

  // 绘制复数上下柱状图
  drawUpDownHistogramChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    let rectWidth = ((that.contentWidth / that.chartData.length) * 0.7) / 2;
    rectWidth = rectWidth > 60 ? 60 : rectWidth;

    const chart = that.content.select(`.${className}`);
    // 中间坐标值
    const centerCount = (that.yScales[chartName].domain()[0] + that.yScales[chartName].domain()[1]) / 2;

    chart.remove();
    const group = that.content.append('g').attr('class', className);

    that.chartData.forEach((item) => {
      const startX = that.xScale(item[that.xAxisKey]) - (item.data.length * rectWidth) / 2;
      item.data.forEach((d, index) => {
        group
          .append('rect')
          .attr('class', 'rect')
          .attr('x', () => {
            return startX + index * rectWidth;
          })
          .attr('y', () => {
            const value = ['', undefined, null, 0].includes(getDataValue(attr, d, index))
              ? 50
              : that.valueCorrect(attr, getDataValue(attr, d, index));
            if (value >= centerCount) {
              return that.contentHeight - that.yScales[chartName](value);
            } else {
              return that.contentHeight / 2;
            }
          })
          .attr('width', rectWidth)
          .attr('height', () => {
            let height = 0;
            const value = ['', undefined, null, 0].includes(getDataValue(attr, d, index))
              ? 50
              : that.valueCorrect(attr, getDataValue(attr, d, index));
            if (value >= centerCount) {
              height = that.yScales[chartName](value) - that.contentHeight / 2;
            } else {
              height = that.contentHeight / 2 - that.yScales[chartName](value);
            }
            if (height === 0) {
              height = 1;
            }
            return height;
          })
          .attr('rx', 2)
          .attr('ry', 2)
          .attr('fill', () => {
            const value = that.valueCorrect(attr, getDataValue(attr, d, index));
            if (value >= centerCount) {
              return that.getFillColor(attr.upColor || attr.color, d.dataHadSelect ? '' : 0.4);
            } else {
              return that.getFillColor(attr.downColor || attr.color, d.dataHadSelect ? '' : 0.4);
            }
          });
      });
    });
  }

  // 绘制复数柱状图
  drawPluralityRect(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    let rectWidth = ((that.contentWidth / that.chartData.length) * 0.7) / 2;
    rectWidth = rectWidth > 60 ? 60 : rectWidth;

    const chart = that.content.select(`.${className}`);

    chart.remove();
    const group = that.content.append('g').attr('class', className);
    that.chartData.forEach((item) => {
      const startX = that.xScale(item[that.xAxisKey]) - (item.data.length * rectWidth) / 2;
      item.data.forEach((d, i) => {
        group
          .append('rect')
          .attr('class', 'rect')
          .attr('x', () => {
            return startX + i * rectWidth;
          })
          .attr('y', () => {
            let height = 0;
            if (attr.y && attr.y instanceof Function) {
              height = that.contentHeight - attr.y(d, i, that, attr);
            } else {
              const dataValue=getDataValue(attr, d, i)
              height = that.yScales[chartName](that.valueCorrect(attr, dataValue));
            }
            if (height <= 0) {
              height = 1;
            }
            const yValue=that.contentHeight - height;
            return yValue
          })
          .attr('width', rectWidth)
          .attr('height', () => {
            let height = 0;
            if (attr.y && attr.y instanceof Function) {
              height = that.contentHeight - attr.y(d, i, that, attr);
            } else {
              height = that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
            }
            if (height <= 0) {
              height = 1;
            }
            return height;
          })
          .attr('fill', () => {
            return that.getFillColor(attr.color, d.colorOpacity);
          })
          .on('click', () => {
            if (!that.onRectClick) {
              return;
            }
            that.onRectClick(d);
          });
      });
    });
  }

  // 画暂停区间线
  drawPauseLine(pauseList = []) {
    const that = this;
    let pause = that.svg.select('.pause');

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    if (that.isEmptySelect(pause)) {
      // 没有创建过暂停区间分组
      pause.remove();
      pause = that.svg.append('g').attr('class', 'pause').attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);

      for (let n = 0, length = pauseList.length; n < length; n++) {
        const item = pauseList[n];
        pause
          .append('path')
          .attr('class', 'pause-line')
          .attr(
            'd',
            lineFun([
              {
                x: that.xScale(item[that.xAxisKey]),
                y: 0
              },
              {
                x: that.xScale(item[that.xAxisKey]),
                y: that.contentHeight
              }
            ])
          )
          .attr('fill', 'none')
          .attr('stroke-width', 1)
          .attr('stroke', that.getFillColor('#808080'))
          .attr('stroke-dasharray', '10,10');
      }
    } else {
    }
  }

  // 十字线
  drawCrossHair(e) {
    const that = this;
    const clientRect = that.el.getBoundingClientRect();
    let x = e.clientX - clientRect.left - that.margin.left;
    let y = e.clientY - clientRect.top - that.margin.top;

    let rectWidth = ((that.contentWidth / that.chartData.length) * 0.7) / 2;
    rectWidth = rectWidth > 60 ? 60 : rectWidth;

    if (x < 0) {
      x = 0;
    } else if (x > that.contentWidth) {
      x = that.contentWidth;
    }

    if (y < 0) {
      y = 0;
    } else if (y > that.contentHeight) {
      y = that.contentHeight;
    }

    // 使用比例尺反向获取该坐标对应的值
    const baseXm = that.xScale.invert(x);
    // 获取数据中最接近的x的值
    const nearlyData = that.getSimilarXValue(this.getValueFromXScale(x));
    const xm = nearlyData.data[that.xAxisKey];

    if (xm === 0 || xm > that.chartData.length) {
      that.deleteCrossHair();
      return;
    }

    // 获取该x值下的数据信息
    const xTickInfo = that.xTickInfo(xm);
    const xValueInfo = nearlyData.data;

    if (!xValueInfo) {
      return;
    }

    let info = xValueInfo.data[0];
    let crossHairX = that.xScale(xValueInfo[that.xAxisKey]);

    if (xValueInfo.data.length === 2) {
      if (baseXm <= xm) {
        crossHairX = crossHairX - rectWidth / 2;
        info = xValueInfo.data[0];
      } else {
        crossHairX = crossHairX + rectWidth / 2;
        info = xValueInfo.data[1];
      }
    }

    if (that.crossHairCallback) {
      that.crossHairCallback(e, info);
    }

    // 绘制十字线的横竖线
    that.drawCrossHairLine(crossHairX, y);
    // 绘制属性Tooltip
    // that.drawAttrTooltip(x, y, xTickInfo.text, info)
    that.drawAttrTooltip(x, y, xTickInfo.text, {
      index: nearlyData.xGroupIndex,
      data: info
    });
  }
}
